import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { getFrameworkId } from "@/data/knowledgeBase";
import { Brain, Target, Users, Sparkles, TrendingUp, Shield, Compass, BarChart3, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { trackPageSection } from "@/lib/analytics";

export const ExpertiseSection = () => {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackPageSection("strategic_disciplines", "view");
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  
  const frameworks = [
    { 
      category: "Strategic Planning & Market Intelligence", 
      description: "Designing strategic direction through market structure, competitive dynamics, and deliberate planning frameworks that guide executive decision-making.",
      items: ["OGSM Framework", "Porter's Five Forces", "Value Chain Analysis", "Market Segmentation Strategy", "Competitive Positioning"],
      icon: Compass,
      color: "from-blue-500/20 to-cyan-500/20",
      borderColor: "border-blue-500/30"
    },
    { 
      category: "Growth & Revenue Architecture", 
      description: "Architecting sustainable growth systems grounded in unit economics, revenue mechanics, and long-term value creation.",
      items: ["Unit Economics", "LTV:CAC Modeling", "Cohort Analysis", "North Star Metrics", "ICE Prioritization", "AARRR Funnel"],
      icon: TrendingUp,
      color: "from-green-500/20 to-emerald-500/20",
      borderColor: "border-green-500/30"
    },
    { 
      category: "Revenue Operations & Go-To-Market Systems", 
      description: "Aligning marketing, sales, and customer functions into a cohesive revenue engine that scales predictably and operates with clarity.",
      items: ["Revenue Orchestration", "Go-To-Market Strategy Design", "Sales–Marketing Alignment", "Pipeline Architecture", "Forecasting Systems"],
      icon: BarChart3,
      color: "from-indigo-500/20 to-violet-500/20",
      borderColor: "border-indigo-500/30"
    },
    { 
      category: "Behavioral Science & Decision Systems", 
      description: "Applying behavioral science to design decision environments that reduce friction, shape choice, and improve outcomes at scale.",
      items: ["Jobs-to-Be-Done (JTBD)", "Fogg Behavior Model", "Choice Architecture", "Loss Aversion", "System 1 / System 2 Thinking", "Cialdini's Principles"],
      icon: Brain,
      color: "from-purple-500/20 to-pink-500/20",
      borderColor: "border-purple-500/30"
    },
    { 
      category: "Customer Intelligence & Experience Design", 
      description: "Translating customer insight into segmentation, positioning, and experience design that supports growth and retention.",
      items: ["Voice of Customer", "Identity-Based Behavior", "Values Mapping", "Customer Journey Orchestration", "Persona Development Systems"],
      icon: Users,
      color: "from-orange-500/20 to-red-500/20",
      borderColor: "border-orange-500/30"
    },
    { 
      category: "Data & AI Decision Systems", 
      description: "Designing data, analytics, and AI governance systems that support explainable, repeatable, and risk-aware decision-making.",
      items: ["Measurement Architecture", "Data Governance", "AI Use Policies", "Feedback Loops"],
      icon: Sparkles,
      color: "from-accent/20 to-primary/20",
      borderColor: "border-accent/30"
    },
    { 
      category: "Executive Leadership & Team Systems", 
      description: "Creating organizational clarity, decision rights, and execution models that scale beyond individuals and withstand growth complexity.",
      items: ["Operating Models", "Decision Rights", "Incentive Design", "Capability Maturity"],
      icon: Shield,
      color: "from-teal-500/20 to-cyan-500/20",
      borderColor: "border-teal-500/30"
    },
  ];

  const toggleExpanded = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
    if (expandedCategory !== category) {
      trackPageSection(category, "interact");
    }
  };

  return (
    <section ref={sectionRef} className="container mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            Strategic Principles & Applied Disciplines
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto mb-3 sm:mb-4 font-medium">
            These are not frameworks for display. They are disciplines I apply depending on the problem in front of me.
          </p>
          <p className="text-base sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-3 sm:mb-4">
            My work is guided by a small set of governing principles that ensure strategy is meaningful, credible, human-centered, and outcome-driven.
          </p>
          <p className="text-sm sm:text-lg text-muted-foreground max-w-3xl mx-auto">
            The disciplines below reflect how those principles are applied in practice — across growth strategy, customer behavior, and execution.
          </p>
        </div>

        {/* Mind Map Journey Visualization */}
        <div className="relative">
          {/* Central Hub - Hidden on mobile, shown on tablet+ */}
          <div className="hidden sm:flex justify-center mb-12 md:mb-16">
            <div className="glass-strong p-6 md:p-8 border-2 border-primary/40 relative z-10 shadow-2xl">
              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center border-2 border-primary/30">
                  <Brain className="w-6 h-6 md:w-8 md:h-8 text-primary animate-pulse" />
                </div>
                <h3 className="text-lg md:text-xl font-bold">Strategic</h3>
                <h3 className="text-lg md:text-xl font-bold">Disciplines</h3>
              </div>
            </div>
          </div>

          {/* Framework Categories Grid */}
          <aside aria-label="Strategic Disciplines">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8 relative">
            {frameworks.map((framework, index) => {
              const Icon = framework.icon;
              const isHovered = hoveredCategory === framework.category;
              const isExpanded = expandedCategory === framework.category;
              
              return (
                <div 
                  key={framework.category}
                  className="relative"
                  onMouseEnter={() => setHoveredCategory(framework.category)}
                  onMouseLeave={() => setHoveredCategory(null)}
                >
                  {/* Connecting Line Animation - Hidden on mobile */}
                  <div 
                    className={`hidden sm:block absolute -top-12 md:-top-16 left-1/2 w-0.5 h-12 md:h-16 bg-gradient-to-b ${
                      isHovered ? 'from-primary via-accent to-transparent' : 'from-muted to-transparent'
                    } transition-all duration-500 ${isHovered ? 'opacity-100 animate-pulse' : 'opacity-30'}`}
                  />
                  
                  <div className={`glass border-2 ${framework.borderColor} p-4 sm:p-5 md:p-6 transition-all duration-200 ease-out h-full ${
                    isHovered ? 'shadow-2xl sm:scale-[1.02] sm:-translate-y-1' : 'shadow-lg'
                  }`}>
                    {/* Category Header - Clickable on mobile */}
                    <div 
                      className="flex items-start gap-3 mb-3 sm:mb-4 cursor-pointer sm:cursor-default"
                      onClick={() => toggleExpanded(framework.category)}
                    >
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 shrink-0 bg-gradient-to-br ${framework.color} flex items-center justify-center border ${framework.borderColor}`}>
                        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 ${isHovered ? 'scale-110' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm sm:text-base leading-tight">{framework.category}</h3>
                      </div>
                      {/* Expand indicator on mobile */}
                      <ChevronDown className={`w-5 h-5 sm:hidden text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>

                    {/* Description - Always visible on desktop, collapsible on mobile */}
                    <div className={`overflow-hidden transition-all duration-200 ${
                      isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 sm:max-h-96 sm:opacity-100'
                    }`}>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 leading-relaxed">
                        {framework.description}
                      </p>

                      {/* Framework Items */}
                      <ul className="space-y-1.5 sm:space-y-2 list-none m-0 p-0">
                        {framework.items.map((item, itemIndex) => (
                          <li key={item}>
                            <Link
                              to={`/knowledge/${getFrameworkId(item)}`}
                            >
                              <div className={`group relative pl-5 sm:pl-6 py-1.5 sm:py-2 transition-all duration-200 ${
                                isHovered ? 'sm:translate-x-2' : ''
                              }`}>
                                {/* Step Number */}
                                <div className="absolute left-0 top-1.5 sm:top-2 w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-[10px] sm:text-xs font-bold border border-primary/30">
                                  {itemIndex + 1}
                                </div>
                                
                                <Badge 
                                  variant="outline" 
                                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-all duration-200 hover:shadow-lg active:scale-95 text-[10px] sm:text-xs py-1 px-2"
                                >
                                  {item}
                                </Badge>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>

                      {/* Connection Indicator - Desktop only */}
                      <div className={`hidden sm:block mt-4 pt-4 border-t border-border/30 text-center transition-opacity duration-300 ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                      }`}>
                        <p className="text-xs text-primary font-semibold">
                          Click any framework to learn more
                        </p>
                      </div>
                    </div>

                    {/* Mobile tap hint when collapsed */}
                    <p className={`sm:hidden text-xs text-muted-foreground text-center mt-2 transition-opacity duration-200 ${
                      isExpanded ? 'opacity-0 hidden' : 'opacity-100'
                    }`}>
                      Tap to expand
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          </aside>
        </div>

      </div>
    </section>
  );
};
